import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  removeCartAction,
  updateCartItemsAction,
  updateCartQtyAction,
} from "../redux/actions/cart";

import { productionDetailAction } from "../redux/actions/products";
import helper from "../Custom/CustomHelpers";
import { getFirstImage } from "../utils/cartHelper";

const extractProduct = (response) => {
  if (!response) return null;
  const candidates = [
    response?.data?.product,
    response?.data?.data,
    response?.data,
    response?.product,
    response?.result,
    response,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    if (candidate.id !== undefined || candidate.productId !== undefined) {
      return candidate;
    }
  }
  return null;
};

const getProductPrices = (product) => {
  if (!product) return [];
  if (Array.isArray(product.prices)) return product.prices;
  if (Array.isArray(product.productPrices)) return product.productPrices;
  if (Array.isArray(product.priceOptions)) return product.priceOptions;
  if (Array.isArray(product.data?.prices)) return product.data.prices;
  return [];
};

const findSelectedPrice = (product, cartItem) => {
  if (!product || Number(cartItem?.priceType) !== 1) return null;
  const prices = getProductPrices(product);
  const cartUnitType = cartItem?.unitType;
  const cartUnitValue = cartItem?.unitValue;
  if (cartUnitType === null || cartUnitType === undefined) return null;
  if (cartUnitValue === null || cartUnitValue === undefined) return null;

  return (
    prices.find(
      (price) =>
        Number(price?.unitType) === Number(cartUnitType) &&
        Number(price?.unitValue) === Number(cartUnitValue),
    ) || null
  );
};

const calculatePrice = (product, selectedPrice, quantity) => {
  const priceType = Number(product?.priceType);
  let basePrice = 0;

  if (priceType === 1) {
    basePrice = Number(selectedPrice?.price ?? 0);
  } else {
    basePrice = Number(product?.basePrice ?? 0);
  }
  if (!Number.isFinite(basePrice)) basePrice = 0;

  let discountedPrice = basePrice;
  const offer = product?.offer || product?.offers || product?.discountOffer;

  // Match ProductItems' getProductItemPrice exactly:
  // only "Percentage" offers apply an inline discount here.
  // "Quantity" offers are intentionally left at full price,
  // same as the product card.
  if (offer?.isActive && offer.offerType === "Percentage") {
    const offerValue = Number(offer.offerValue ?? 0);
    discountedPrice = basePrice - (basePrice * offerValue) / 100;
  }

  discountedPrice = Math.max(0, Number(discountedPrice.toFixed(2)));
  return { basePrice, discountedPrice };
};

const Cart = ({ closeCart }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart?.cartItems || []);

  const [isRefreshing, setIsRefreshing] = useState(true);
  const [refreshError, setRefreshError] = useState("");
  const [invalidItems, setInvalidItems] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const refreshCart = async () => {
      if (!cartItems.length) {
        setIsRefreshing(false);
        setInvalidItems([]);
        return;
      }

      setIsRefreshing(true);
      setRefreshError("");
      setInvalidItems([]);

      const refreshedItems = [];
      const invalid = [];

      for (const cartItem of cartItems) {
        try {
          if (!cartItem?.productId) {
            invalid.push({
              cartKey: cartItem?.cartKey,
              message: "Product ID is missing.",
            });
            continue;
          }

          const response = await dispatch(
            productionDetailAction(cartItem.productId),
          );
          if (cancelled) return;

          const product = extractProduct(response);
          if (!product) {
            invalid.push({
              cartKey: cartItem.cartKey,
              message: "Product could not be loaded.",
            });
            continue;
          }

          if (product.isPublic === false) {
            invalid.push({
              cartKey: cartItem.cartKey,
              message: `${product.title || "This product"} is no longer available.`,
            });
            continue;
          }

          if (product.isSoldOut === true) {
            invalid.push({
              cartKey: cartItem.cartKey,
              message: `${product.title || "This product"} is sold out.`,
            });
            continue;
          }

          let selectedPrice = null;
          if (Number(cartItem.priceType) === 1) {
            selectedPrice = findSelectedPrice(product, cartItem);
            if (!selectedPrice) {
              invalid.push({
                cartKey: cartItem.cartKey,
                message: `${product.title || "This product"}: the selected size/unit is no longer available.`,
              });
              continue;
            }
          }

          const quantity = Math.max(1, Number(cartItem.quantity) || 1);
          const { basePrice, discountedPrice } = calculatePrice(
            product,
            selectedPrice,
            quantity,
          );

          refreshedItems.push({
            cartKey: cartItem.cartKey,
            productId: cartItem.productId,
            quantity,
            priceType: cartItem.priceType,
            unitType: cartItem.unitType ?? null,
            unitValue: cartItem.unitValue ?? null,
            title: product.title || "",
            image: getFirstImage(product.image),
            description: product.description || "",
            basePrice: product.basePrice ?? 0,
            prices: getProductPrices(product),
            offer: product.offer || null,
            selectedPrice,
            currentBasePrice: basePrice,
            discountedPrice,
          });
        } catch (error) {
          console.error(
            `Failed to refresh product ${cartItem?.productId}:`,
            error,
          );
          invalid.push({
            cartKey: cartItem?.cartKey,
            message: `Unable to refresh ${cartItem?.productId ? `product ${cartItem.productId}` : "this product"}.`,
          });
        }
      }

      if (cancelled) return;

      dispatch(updateCartItemsAction(refreshedItems));
      setInvalidItems(invalid);
      setIsRefreshing(false);
      if (invalid.length > 0)
        setRefreshError("Some cart items are no longer available.");
    };

    refreshCart();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getItemPrice = (item) => {
    const price = Number(
      item?.discountedPrice ?? item?.currentBasePrice ?? item?.basePrice ?? 0,
    );
    return Number.isFinite(price) ? price : 0;
  };

  const subtotal = useMemo(() => {
    return cartItems.reduce((total, item) => {
      const price = getItemPrice(item);
      const quantity = Number(item?.quantity) || 1;
      return total + price * quantity;
    }, 0);
  }, [cartItems]);

  const handleQuantity = (cartKey, action) => {
    dispatch(updateCartQtyAction(cartKey, action));
  };

  const handleRemove = (cartKey) => {
    dispatch(removeCartAction(cartKey));
  };

  const handleCheckout = () => {
    if (isRefreshing || invalidItems.length > 0) return;

    if (window.fbq && cartItems.length > 0) {
      window.fbq("track", "InitiateCheckout", {
        value: subtotal,
        currency: "PKR",
        contents: cartItems.map((item) => ({
          id: item.productId,
          quantity: item.quantity,
        })),
        content_type: "product",
        num_items: cartItems.length,
      });
    }

    if (closeCart) closeCart();
    navigate("/checkout");
  };

  const handleContinueShopping = () => {
    if (closeCart) closeCart();
    navigate("/");
  };

  return (
    <div
      className="
        fixed z-[1000]
        bg-custom-white text-custom-dark dark:text-custom-white dark:bg-custom-dark
        border border-gray-500 rounded-lg shadow
        overflow-y-auto

        /* Mobile */
        inset-x-3 top-1/2 -translate-y-1/2
        w-auto max-w-none
        h-[70vh]
        p-4

        /* Desktop */
        md:right-2 md:left-auto md:inset-x-auto
        md:top-24 md:translate-y-0
        md:w-3/4 md:max-w-md
        md:h-4/6
        md:p-4
        md:sm:p-8
      "
    >
      <div className="flex items-center justify-between mb-4">
        <h5 className="text-xl font-bold leading-none select-none">
          Shopping Cart
        </h5>
        {closeCart && (
          <AiOutlineClose className="cursor-pointer" onClick={closeCart} />
        )}
      </div>

      {isRefreshing ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          Refreshing cart...
        </p>
      ) : cartItems.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-gray-500 mb-3">Your cart is empty.</p>
          <button
            type="button"
            onClick={handleContinueShopping}
            className="font-medium text-custom-dark-green hover:text-green-700"
          >
            Continue Shopping <span aria-hidden="true"> &rarr;</span>
          </button>
        </div>
      ) : (
        <>
          {refreshError && (
            <div className="mb-4 rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium">{refreshError}</p>
              {invalidItems.map((item) => (
                <div key={item.cartKey} className="mt-1">
                  {item.message}
                </div>
              ))}
            </div>
          )}

          {cartItems.map((item) => {
            const price = getItemPrice(item);
            const quantity = Number(item.quantity) || 1;
            const hasDiscount = item.discountedPrice < item.currentBasePrice;

            return (
              <div className="flow-root mb-4" key={item.cartKey}>
                <ul className="divide-y divide-gray-200 dark:divide-gray-800">
                  <li className="py-3 sm:py-4">
                    <div className="flex items-start gap-3 sm:items-center sm:gap-4">
                      <div className="flex-shrink-0">
                        {item.image ? (
                          <img
                            className="w-16 h-16 object-contain"
                            src={item.image}
                            alt={item.title || "Product"}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-16 h-16 flex items-center justify-center text-xs text-gray-400 border rounded">
                            No Image
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p
                          className="text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis"
                          title={item.title || "Product"}
                        >
                          {item.title || "Product"}
                        </p>
                        {item.description && (
                          <p className="text-sm text-gray-400 truncate">
                            {item.description}
                          </p>
                        )}

                        <div className="mt-2 flex items-center gap-2">
                          <button
                            aria-label="decrease quantity"
                            disabled={quantity <= 1}
                            onClick={() =>
                              handleQuantity(item.cartKey, "decrease")
                            }
                            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            -
                          </button>
                          <span>{quantity}</span>
                          <button
                            aria-label="increase quantity"
                            disabled={quantity >= 5}
                            onClick={() =>
                              handleQuantity(item.cartKey, "increase")
                            }
                            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-col items-end text-right">
                        <div className="flex flex-col items-end">
                          {hasDiscount && (
                            <span className="text-sm text-red-500 line-through">
                              Rs. {item.currentBasePrice.toFixed(2)}
                            </span>
                          )}
                          <span className="text-base font-semibold text-green-600">
                            Rs. {price.toFixed(2)}
                          </span>
                          {item.unitValue !== null &&
                            item.unitValue !== undefined && (
                              <span className="text-xs text-gray-500">
                                {item.unitValue}{" "}
                                {helper.UNIT_TYPE_LABEL?.[item.unitType]}
                              </span>
                            )}
                        </div>

                        <button
                          aria-label="Remove Item from Cart"
                          className="text-red-500 underline text-sm mt-1"
                          onClick={() => handleRemove(item.cartKey)}
                        >
                          <FaTrash className="hover:text-red-700 cursor-pointer" />
                        </button>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            );
          })}

          <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
            <div className="flex justify-between text-base font-medium text-gray-900 dark:text-custom-white">
              <p>Subtotal</p>
              <p>{subtotal.toFixed(2)} Rs</p>
            </div>
            <p className="mt-0.5 text-sm text-gray-500">
              Shipping and taxes calculated at checkout.
            </p>
            <div className="mt-6">
              <button
                disabled={isRefreshing || invalidItems.length > 0}
                onClick={handleCheckout}
                className={`flex items-center justify-center w-full rounded-md px-6 py-3 text-base font-medium ${
                  isRefreshing || invalidItems.length > 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-custom-green hover:bg-custom-dark-green"
                }`}
              >
                Checkout
              </button>
            </div>
            <div className="mt-2 flex justify-center text-center text-sm text-gray-500 flex-col">
              <p>or</p>
              <p>
                <button
                  aria-label="Continue Shopping"
                  type="button"
                  className="font-medium text-custom-dark-green hover:text-green-700 mt-2"
                  onClick={handleContinueShopping}
                >
                  Continue Shopping <span aria-hidden="true"> &rarr;</span>
                </button>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
